import { parseISO } from "date-fns";
import {
    RuleConditionAmount,
    RuleConditionDate,
    RuleConditionType,
    TransactionRule,
    TransactionRuleCondition,
} from "../../types/transactionRule";
import {
    ParsedSimilarToValue,
    isRuleConditionSimilarTo,
    parseSimilarToValue,
} from "../split-aware-description.helper";
import {
    IndexedCondition,
    IndexedRule,
    RuleScopeContext,
    RuleConditionValue,
    SCOPE_TYPES,
    CATEGORY_TYPES,
    bucketKey,
    normalizeValueArray,
} from "./types";

function indexScopeCondition(
    condition: TransactionRuleCondition,
    values: readonly string[],
    ctx: RuleScopeContext,
): Pick<
    IndexedCondition,
    "scopeEntities" | "scopeAccounts" | "scopeSubaccounts"
> {
    if (condition.conditionType === RuleConditionType.ENTITY) {
        const entities = new Set<number>();
        for (const v of values) {
            const id = Number(v);
            if (!Number.isNaN(id)) {
                entities.add(id);
            }
        }
        return { scopeEntities: entities };
    }

    if (condition.conditionType === RuleConditionType.FINANCIAL_ACCOUNTS) {
        const entities = new Set<number>();
        const accounts = new Set<number>();
        for (const v of values) {
            const id = Number(v);
            if (Number.isNaN(id)) {
                continue;
            }
            accounts.add(id);
            const entityId = ctx.financialAccountEntityId.get(id);
            if (entityId !== undefined && entityId !== null) {
                entities.add(entityId);
            }
        }
        return { scopeEntities: entities, scopeAccounts: accounts };
    }

    if (condition.conditionType === RuleConditionType.SUBACCOUNTS) {
        const entities = new Set<number>();
        const accounts = new Set<number>();
        const subaccounts = new Set<string>();
        for (const v of values) {
            subaccounts.add(v);
            const faId = ctx.subaccountFinancialAccountId.get(v);
            if (faId === undefined) {
                continue;
            }
            accounts.add(faId);
            const entityId = ctx.financialAccountEntityId.get(faId);
            if (entityId !== undefined && entityId !== null) {
                entities.add(entityId);
            }
        }
        return {
            scopeEntities: entities,
            scopeAccounts: accounts,
            scopeSubaccounts: subaccounts,
        };
    }

    return {};
}

// SIMILAR_TO carries two value shapes: a bare description id (legacy) and the
// split-keyed object. `normalizeValueArray` flattens the object to an empty set,
// which made every object-shaped condition compare equal to every other one.
function indexSimilarToValue(
    value: RuleConditionValue,
): ParsedSimilarToValue | undefined {
    if (typeof value === "string") {
        return parseSimilarToValue(value);
    }
    if (isRuleConditionSimilarTo(value)) {
        return parseSimilarToValue(value);
    }
    return undefined;
}

function indexCondition(
    condition: TransactionRuleCondition,
    ctx: RuleScopeContext,
): IndexedCondition {
    const values = normalizeValueArray(condition.value);
    if (SCOPE_TYPES.has(condition.conditionType)) {
        return {
            condition,
            values,
            ...indexScopeCondition(condition, values, ctx),
        };
    }
    if (CATEGORY_TYPES.has(condition.conditionType)) {
        return { condition, values, categoryRaw: new Set<string>(values) };
    }
    if (condition.conditionType === RuleConditionType.SIMILAR_TO) {
        return {
            condition,
            values,
            similarTo: indexSimilarToValue(condition.value),
        };
    }
    return { condition, values };
}

export function indexRule(
    rule: TransactionRule,
    ctx: RuleScopeContext,
): IndexedRule {
    const buckets = new Map<string, IndexedCondition[]>();
    for (const condition of rule.conditions) {
        const key = bucketKey(
            condition.conditionType,
            condition.transferDirection,
        );
        const bucket = buckets.get(key);
        const indexed = indexCondition(condition, ctx);
        if (bucket) {
            bucket.push(indexed);
        } else {
            buckets.set(key, [indexed]);
        }
    }
    return {
        rule,
        buckets,
        conditionBucketKeys: new Set(buckets.keys()),
    };
}

export function isAmountValueSubset(
    lower: RuleConditionAmount,
    higher: RuleConditionAmount,
): boolean {
    const minOk =
        higher.min === undefined ||
        (lower.min !== undefined && higher.min <= lower.min);
    const maxOk =
        higher.max === undefined ||
        (lower.max !== undefined && higher.max >= lower.max);
    return minOk && maxOk;
}

function parseDateBoundary(value: string | undefined): number | undefined {
    if (value === undefined) {
        return undefined;
    }
    const parsed = parseISO(value);
    const time = parsed.getTime();
    return Number.isNaN(time) ? undefined : time;
}

export function isDateValueSubset(
    lower: RuleConditionDate,
    higher: RuleConditionDate,
): boolean {
    const lowerAfter = parseDateBoundary(lower.after);
    const lowerBefore = parseDateBoundary(lower.before);
    const higherAfter = parseDateBoundary(higher.after);
    const higherBefore = parseDateBoundary(higher.before);

    const afterOk =
        higherAfter === undefined ||
        (lowerAfter !== undefined && higherAfter <= lowerAfter);
    const beforeOk =
        higherBefore === undefined ||
        (lowerBefore !== undefined && higherBefore >= lowerBefore);
    return afterOk && beforeOk;
}

export function isCategoryValueInHigherSet(
    lowerValue: string,
    higherRaw: ReadonlySet<string>,
): boolean {
    return higherRaw.has(lowerValue);
}
